import { Configuration } from "../configuration";
import { ApiError, OptionItem } from "../models";
import { genericApi } from "./fetchHelper";

export const sortOptions = <T extends string | number>(a: OptionItem<T>, b: OptionItem<T>) =>
  (a.label || "").localeCompare(b.label || "", "en", { numeric: true });

export const getOptions = <T extends string | number>(
  optionValues: Array<T | { key: T; value: string } | { Key: T; Value: string }> | { [key: string]: string },
  optionValueType: "string" | "number",
  defaultOptionLabel?: string,
  applySort: boolean = true,
) => {
  let options: Array<OptionItem<T>> = [];
  if (Array.isArray(optionValues)) {
    optionValues.forEach(optionValue => {
      let optionValueValue: T;
      let optionValueLabel: string;
      if (typeof optionValue === "object") {
        // If KeyValuePair is serialized via .NET5+...
        if ((optionValue as any).key) {
          const optionValueKeyValuePair = optionValue as { key: T; value: string };
          optionValueValue = optionValueKeyValuePair.key;
          optionValueLabel = optionValueKeyValuePair.value;
        }
        // Otherwise, KeyValuePair is serialized via .NET version prior to support for Property Naming Policy...
        // https://github.com/dotnet/docs/issues/20898
        else {
          const optionValueKeyValuePair = optionValue as { Key: T; Value: string };
          optionValueValue = optionValueKeyValuePair.Key;
          optionValueLabel = optionValueKeyValuePair.Value;
        }
      } else {
        optionValueValue = optionValue;
        optionValueLabel = `${optionValue}`;
      }
      options.push({
        value: optionValueValue,
        label: optionValueLabel,
      });
    });
  } else if (optionValues !== null && typeof optionValues === "object") {
    Object.keys(optionValues).forEach((optionValueKey: string) => {
      options.push({
        value: optionValueType === "number" ? (parseInt(`${optionValueKey}`, 10) as T) : (optionValueKey as T),
        label: (optionValues as any)[optionValueKey],
      });
    });
  }
  // If options exist...
  if (options.length) {
    if (applySort) {
      options = options.sort(sortOptions);
    }
    // If there is a default option to add at the top of the list...
    if (defaultOptionLabel) {
      // Casting as any here since we want an empty string as the select option value in the DOM, even if the option
      // values are numeric
      options.unshift({ value: "", label: defaultOptionLabel } as OptionItem<T>);
    }
  }
  return options;
};

export const fetchOptions = <T extends string | number>(
  fetchUrl: string,
  optionValueType: "string" | "number",
  defaultOptionLabel?: string,
  applySort: boolean = true,
  getApiConfiguration?: () => Promise<Configuration>,
): Promise<Array<OptionItem<T>>> => {
  return new Promise<Array<OptionItem<T>>>((resolve, reject) => {
    if (getApiConfiguration) {
      genericApi
        .get(fetchUrl, getApiConfiguration)
        .then(
          (
            optionValues: Array<T | { key: T; value: string } | { Key: T; Value: string }> | { [key: string]: string },
          ) => {
            const options = getOptions<T>(optionValues, optionValueType, defaultOptionLabel, applySort);
            resolve(options);
          },
        )
        .catch((error: ApiError) => {
          reject(`The following error occurred upon attempting to fetch Options: ${error}`);
        });
    } else {
      reject("No API Configuration resolver was provided for fetching Options.");
    }
  });
};

export const postOption = <T extends string | number>(
  url: string,
  optionLabel: string,
  getApiConfiguration?: () => Promise<Configuration>,
): Promise<OptionItem<T>> => {
  return new Promise<OptionItem<T>>((resolve, reject) => {
    if (getApiConfiguration) {
      const addOptionCommand = { value: optionLabel };
      genericApi
        .post(url, addOptionCommand, getApiConfiguration)
        .then((optionKey: T) => {
          resolve({ value: optionKey, label: optionLabel });
        })
        .catch((error: ApiError) => {
          reject(`The following error occurred upon attempting to add an Option: ${error}`);
        });
    } else {
      reject("No API Configuration resolver was provided for adding an Option.");
    }
  });
};
